import { useMemo, useState } from 'react'
import { ExternalUserServiceIntegration } from '../../../../../models/User'
import { ClaimPhoneNumberPage } from '../../../Connectors/ModalIntegrationConnectors/Phone/models'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Button } from '../../../../../components/Button/Button'
import { DeletePhoneIntegrationModal } from '../../../Connectors/ModalIntegrationConnectors/Phone/DeletePhoneIntegrationModal'
import { PhoneNumber } from '../../../../../components/PhoneNumber/PhoneNumber'
import { Divider } from '../../../../../components/Divider/Divider'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../../../store/user/user'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { ClaimPhoneNumberModal } from '../../../Connectors/ModalIntegrationConnectors/Phone/ClaimPhoneNumberModal'

export const PhoneNumbers: React.FC = () => {
  const [selectedUserIntegration, setSelectedUserIntegration] = useState<ExternalUserServiceIntegration | null>(null)
  const [showDeletePhoneIntegrationModal, setShowDeletePhoneIntegrationModal] = useState<boolean>(false)

  const accountId = useAppSelector(selectAccountId)
  const user = useAppSelector(selectAuthenticatedUser)

  const [initialPage, setInitialPage] = useState<ClaimPhoneNumberPage | undefined>(undefined)
  const [initialPhoneNumber, setInitialPhoneNumber] = useState<string | undefined>(undefined)
  const [showClaimPhoneNumberModal, setShowClaimPhoneNumberModal] = useState(false)

  const { data: userData } = useGetMeQuery({ accountId: accountId })
  const phoneIntegrations = useMemo(() => {
    return userData?.integrations?.filter((i) => i.serviceId === IntegrationServiceId.PHONE)
  }, [userData])

  const handleShowClaimPhoneNumberModal = (page: ClaimPhoneNumberPage | undefined = undefined, phoneNumber: string | undefined = undefined) => {
    setInitialPage(page)
    setInitialPhoneNumber(phoneNumber)
    setShowClaimPhoneNumberModal(true)
  }

  const handleHideClaimPhoneNumberModal = () => {
    setShowClaimPhoneNumberModal(false)
    setInitialPage(undefined)
    setInitialPhoneNumber(undefined)
  }

  const handleResendCode = (phoneNumber: string) => {
    handleShowClaimPhoneNumberModal(ClaimPhoneNumberPage.PHONE_NUMBER, phoneNumber)
  }

  const handleVerifyCode = (phoneNumber: string) => {
    handleShowClaimPhoneNumberModal(ClaimPhoneNumberPage.VERIFICATION, phoneNumber)
  }

  const handleDeleteUserIntegration = (integration: ExternalUserServiceIntegration): void => {
    setSelectedUserIntegration(integration)
    setShowDeletePhoneIntegrationModal(true)
  }

  const handleDeletePhoneIntegrationModalHide = () => {
    setShowDeletePhoneIntegrationModal(false)
    setSelectedUserIntegration(null)
  }

  type PhoneItemProps = {
    integration: ExternalUserServiceIntegration
  }
  const PhoneNumberStatus: React.FC<PhoneItemProps> = ({ integration }) => {
    switch (integration.state) {
      case 'live':
        return (
          <div className='pt-2 d-flex justify-content-start zocks-text-success'>
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-check' />
              Validated number
            </Stack>
          </div>
        )
      case 'validating':
        return (
          <div className='pt-2 d-flex justify-content-start zocks-text-warning-text'>
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-exclamation-circle' />
              Invalid number. Please finish the validation.
            </Stack>
          </div>
        )
      case 'invalid':
        return (
          <div className='pt-2 d-flex justify-content-start zocks-text-warning-text'>
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-exclamation-circle' />
              {integration.metadata?.invalidity_reason === 'newer_integration' ? 'Used by another user.' : ''} Please validate the number if you want to use it.
            </Stack>
          </div>
        )
      default:
        return <></>
    }
  }

  const PhoneNumberButtons: React.FC<PhoneItemProps> = ({ integration }) => {
    return (
      <ScopedBlock
        scope={{
          scope: 'integration:connect',
          superScope: ['integration:admin'],
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <Stack direction='horizontal' gap={2}>
          {integration.state === 'invalid' && (
            <Button variant='primary' onClick={() => handleResendCode(integration.externalId)}>
              Resend code
            </Button>
          )}
          {integration.state === 'validating' && (
            <Button variant='primary' onClick={() => handleVerifyCode(integration.externalId)}>
              Verify code
            </Button>
          )}
          <Button variant='primaryGhost' className='zocks-p-0.75' onClick={() => handleDeleteUserIntegration(integration)}>
            <i className='bi bi-trash' />
          </Button>
        </Stack>
      </ScopedBlock>
    )
  }

  return (
    <Container key='phone-numbers-container' className='p-0'>
      {showClaimPhoneNumberModal && userData && (
        <ClaimPhoneNumberModal show={showClaimPhoneNumberModal} onHide={handleHideClaimPhoneNumberModal} page={initialPage} phoneNumber={initialPhoneNumber} />
      )}
      {selectedUserIntegration && user ? (
        <DeletePhoneIntegrationModal
          accountId={accountId}
          userId={user?.id}
          integration={selectedUserIntegration}
          show={showDeletePhoneIntegrationModal}
          onHide={handleDeletePhoneIntegrationModalHide}
        />
      ) : null}
      {phoneIntegrations?.length ? (
        phoneIntegrations.map((i, idx) => (
          <div key={i.id}>
            <Row className='d-flex justify-content-between py-md-3'>
              <Col className='px-md-3'>
                <Stack>
                  <h6>
                    <PhoneNumber phoneNumber={i.externalId} />
                  </h6>
                  <PhoneNumberStatus integration={i} />
                </Stack>
              </Col>
              <Col md='auto' className='d-flex p-0'>
                <PhoneNumberButtons integration={i} />
              </Col>
            </Row>
            {idx + 1 < phoneIntegrations.length ? <Divider key={`${i.id}-divider`} className='d-none d-md-block' /> : ''}
          </div>
        ))
      ) : (
        <div>No connected phone numbers.</div>
      )}
    </Container>
  )
}
